import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import PlanComparison from '../components/PlanComparison';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  User, Settings, CreditCard, Mail, Shield, Bell, 
  Crown, Zap, CheckCircle, ArrowRight, MessageSquare,
  Calendar, FileText, LogOut, Trash2, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Account() {
  const { user, logout, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [messages, setMessages] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [upgrading, setUpgrading] = useState(false);

  // Theme classes - comprehensive
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-blue-600';
  const innerBg = isDark ? 'bg-slate-700' : 'bg-slate-100/50';
  const borderColor = isDark ? 'border-slate-600' : 'border-slate-300';
  const badgeBg = isDark ? 'bg-slate-700 text-blue-300' : 'bg-blue-100 text-blue-800';
  const hoverBg = isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100';
  const tabsBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-300';

  useEffect(() => {
    fetchAccountData();
  }, []);

  useEffect(() => {
    if (showPlanModal && user?.role) {
      fetchAvailablePlans();
    }
  }, [showPlanModal, user?.role]);

  const fetchAvailablePlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/available-plans?role=${user?.role}`);
      setAvailablePlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchAccountData = async () => {
    try {
      const [profileRes, subRes, matchesRes] = await Promise.all([
        axios.get(`${API}/profile/my-profile`),
        axios.get(`${API}/subscription/my-plan`),
        axios.get(`${API}/matches`)
      ]);

      if (profileRes.data.exists) {
        setProfile(profileRes.data.profile);
      }
      setSubscription(subRes.data);
      setMatches(matchesRes.data || []);

      // Fetch recent messages from matches
      if (matchesRes.data && matchesRes.data.length > 0) {
        const recentMessages = [];
        for (const match of matchesRes.data.slice(0, 5)) {
          try {
            const msgRes = await axios.get(`${API}/messages/${match.match_id}`);
            if (msgRes.data && msgRes.data.length > 0) {
              recentMessages.push({
                matchId: match.match_id,
                matchName: match.matched_user.name,
                lastMessage: msgRes.data[msgRes.data.length - 1]
              });
            }
          } catch (e) {
            // Skip if no messages
          }
        }
        setMessages(recentMessages);
      }
    } catch (error) {
      console.error('Error fetching account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleOpenPlanModal = () => {
    setSelectedPlan(null);
    setBillingCycle(subscription?.billing_cycle || 'monthly');
    setShowPlanModal(true);
  };

  const handlePlanChange = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setUpgrading(true);
    try {
      await axios.post(`${API}/subscription/update-plan`, {
        plan_name: selectedPlan.plan_name,
        billing_cycle: billingCycle
      });
      
      toast.success(`Successfully changed to ${selectedPlan.plan_name}!`);
      setShowPlanModal(false);
      
      // Refresh subscription data
      await fetchAccountData();
      
      // Refresh auth context if available
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setUpgrading(false);
    }
  };

  const getPrice = (plan) => {
    if (!plan) return 0;
    return billingCycle === 'yearly' ? plan.yearly_price_usd : plan.monthly_price_usd;
  };

  const getPlanIcon = () => {
    if (!subscription) return <Shield className="text-blue-700" size={24} />;
    const planName = subscription.plan_name?.toLowerCase() || '';
    if (planName.includes('elite') || planName.includes('premium')) {
      return <Crown className="text-blue-600" size={24} />;
    } else if (planName.includes('pro') || planName.includes('plus')) {
      return <Zap className="text-blue-600" size={24} />;
    }
    return <Shield className="text-blue-700" size={24} />;
  };

  const getPlanBadgeColor = () => {
    if (!subscription) return 'bg-slate-200 text-blue-800';
    const planName = subscription.plan_name?.toLowerCase() || '';
    if (planName.includes('elite') || planName.includes('premium')) {
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    } else if (planName.includes('pro') || planName.includes('plus')) {
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    }
    return 'bg-slate-200 text-blue-800';
  };

  const getUpgradePath = () => {
    if (!user) return '/pricing';
    if (user.role === 'founder') return '/pricing/founder';
    if (user.role === 'investor') return '/pricing/investor';
    if (user.role === 'cofounder') return '/pricing/cofounder';
    return '/pricing';
  };

  const getFeatures = () => {
    if (!subscription || !subscription.features) return [];
    return Object.entries(subscription.features)
      .filter(([key, value]) => value === true)
      .map(([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className={textSecondary}>Loading...</div>
        </div>
      </div>
    );
  }

  // Admin-specific account view
  if (user?.role === 'admin') {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className={`text-3xl font-bold mb-2 ${textPrimary}`}>Admin Account</h1>
            <p className={textSecondary}>Manage your admin settings</p>
          </div>

          <Card className={`shadow-sm p-6 mb-6 ${cardBg}`}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center">
                <Shield className="text-blue-500" size={32} />
              </div>
              <div>
                <h2 className={`text-xl font-bold ${textPrimary}`}>{user.name}</h2>
                <p className={textSecondary}>{user.email}</p>
                <span className={`text-xs px-2 py-1 rounded mt-2 inline-block ${isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                  Administrator
                </span>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${innerBg}`}>
                <p className={`text-xs uppercase ${textSecondary}`}>Role</p>
                <p className={`font-medium ${textPrimary}`}>Platform Administrator</p>
              </div>
              <div className={`p-4 rounded-lg ${innerBg}`}>
                <p className={`text-xs uppercase ${textSecondary}`}>Access Level</p>
                <p className={`font-medium ${textPrimary}`}>Full Access</p>
              </div>
            </div>

            <div className={`border-t pt-6 ${borderColor}`}>
              <h3 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Quick Actions</h3>
              <div className="space-y-3">
                <Link to="/dashboard">
                  <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${innerBg} ${hoverBg}`}>
                    <div className="flex items-center gap-3">
                      <User className="text-blue-500" size={20} />
                      <span className={textPrimary}>Manage Users</span>
                    </div>
                    <ArrowRight className={textSecondary} size={16} />
                  </div>
                </Link>
                <Link to="/dashboard">
                  <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${innerBg} ${hoverBg}`}>
                    <div className="flex items-center gap-3">
                      <CreditCard className="text-blue-500" size={20} />
                      <span className={textPrimary}>Manage Subscriptions</span>
                    </div>
                    <ArrowRight className={textSecondary} size={16} />
                  </div>
                </Link>
              </div>
            </div>
          </Card>

          <Card className={`shadow-sm p-6 ${cardBg}`}>
            <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Session</h2>
            <Button onClick={handleLogout} variant="destructive" className="w-full">
              <LogOut className="mr-2" size={16} />
              Sign Out
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg}`}>
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className={`text-3xl font-bold mb-2 ${textPrimary}`}>Account Settings</h1>
          <p className={textSecondary}>Manage your account, subscription, and preferences</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`p-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-100 border-slate-300'}`}>
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <User size={16} className="mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="subscription" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <CreditCard size={16} className="mr-2" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="inbox" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Mail size={16} className="mr-2" />
              Inbox
              {messages.length > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {messages.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Settings size={16} className="mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Profile Card */}
              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <div className="flex items-start justify-between mb-4">
                  <h2 className={`text-xl font-bold ${textPrimary}`}>Profile</h2>
                  <Link to="/profile-setup">
                    <Button variant="ghost" size="sm" className={`${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'}`}>
                      Edit
                    </Button>
                  </Link>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center">
                      <span className="text-2xl font-bold text-blue-500">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>{user?.name || 'User'}</h3>
                      <p className={`text-sm ${textSecondary}`}>{user?.email}</p>
                      <span className={`inline-block mt-1 text-xs px-2 py-1 rounded capitalize ${badgeBg}`}>
                        {user?.role}
                      </span>
                    </div>
                  </div>
                  {profile && (
                    <div className={`pt-4 border-t space-y-2 ${borderColor}`}>
                      {profile.startup_name && (
                        <div className="flex justify-between">
                          <span className={`text-sm ${textSecondary}`}>Company</span>
                          <span className={`text-sm ${textPrimary}`}>{profile.startup_name}</span>
                        </div>
                      )}
                      {profile.city && (
                        <div className="flex justify-between">
                          <span className={`text-sm ${textSecondary}`}>Location</span>
                          <span className={`text-sm ${textPrimary}`}>{profile.city}, {profile.country}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              {/* Subscription Summary Card */}
              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <div className="flex items-start justify-between mb-4">
                  <h2 className={`text-xl font-bold ${textPrimary}`}>Subscription</h2>
                  <Button 
                    onClick={handleOpenPlanModal} 
                    variant="ghost" 
                    size="sm" 
                    className={`${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'}`}
                  >
                    {subscription?.plan_name?.toLowerCase().includes('free') ? 'Upgrade' : 'Change'}
                  </Button>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${innerBg}`}>
                    {getPlanIcon()}
                  </div>
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getPlanBadgeColor()}`}>
                      {subscription?.plan_name || 'Free Plan'}
                    </span>
                    <p className={`text-sm mt-1 ${textSecondary}`}>
                      {(subscription?.plan_price_usd_monthly === 0 || subscription?.plan_price_usd_monthly === undefined || subscription?.plan_price_usd_monthly === null)
                        ? 'Free forever' 
                        : `$${subscription.plan_price_usd_monthly}/month`
                      }
                    </p>
                  </div>
                </div>
                {subscription?.plan_name?.toLowerCase().includes('free') && (
                  <Button onClick={handleOpenPlanModal} className="btn-primary w-full mt-4">
                    Upgrade Your Plan
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                )}
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className={`shadow-sm p-4 text-center ${cardBg}`}>
                <MessageSquare className="text-blue-500 mx-auto mb-2" size={24} />
                <div className={`text-2xl font-bold ${textPrimary}`}>{matches.length}</div>
                <div className={`text-sm ${textSecondary}`}>Matches</div>
              </Card>
              <Card className={`shadow-sm p-4 text-center ${cardBg}`}>
                <Mail className="text-blue-500 mx-auto mb-2" size={24} />
                <div className={`text-2xl font-bold ${textPrimary}`}>{messages.length}</div>
                <div className={`text-sm ${textSecondary}`}>Conversations</div>
              </Card>
              <Card className={`shadow-sm p-4 text-center ${cardBg}`}>
                <Calendar className="text-blue-500 mx-auto mb-2" size={24} />
                <div className={`text-2xl font-bold ${textPrimary}`}>0</div>
                <div className={`text-sm ${textSecondary}`}>Sessions</div>
              </Card>
              <Card className={`shadow-sm p-4 text-center ${cardBg}`}>
                <FileText className="text-blue-500 mx-auto mb-2" size={24} />
                <div className={`text-2xl font-bold ${textPrimary}`}>0</div>
                <div className={`text-sm ${textSecondary}`}>Documents</div>
              </Card>
            </div>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription" className="space-y-6">
            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center">
                    {getPlanIcon()}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-blue-900">{subscription?.plan_name || 'Free Plan'}</h2>
                    <p className="text-blue-700">
                      {subscription?.billing_cycle === 'yearly' ? 'Annual billing' : 'Monthly billing'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">
                    {(subscription?.plan_price_usd_monthly === 0 || subscription?.plan_price_usd_monthly === undefined || subscription?.plan_price_usd_monthly === null)
                      ? '$0' 
                      : `$${subscription.plan_price_usd_monthly}`
                    }
                  </div>
                  <div className="text-blue-700 text-sm">/month</div>
                </div>
              </div>

              {/* Subscription Details */}
              <div className="border-t border-slate-300 pt-6 mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Subscription Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-slate-100/50 p-4 rounded-lg">
                    <p className="text-blue-700 text-xs uppercase tracking-wide mb-1">Plan Name</p>
                    <p className="text-blue-900 font-medium">{subscription?.plan_name || 'Free Plan'}</p>
                  </div>
                  <div className="bg-slate-100/50 p-4 rounded-lg">
                    <p className="text-blue-700 text-xs uppercase tracking-wide mb-1">Billing Cycle</p>
                    <p className="text-blue-900 font-medium capitalize">{subscription?.billing_cycle || 'Monthly'}</p>
                  </div>
                  <div className="bg-slate-100/50 p-4 rounded-lg">
                    <p className="text-blue-700 text-xs uppercase tracking-wide mb-1">Status</p>
                    <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${
                      subscription?.plan_status === 'active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : subscription?.plan_status === 'expired'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-slate-700 text-blue-800'
                    }`}>
                      {subscription?.plan_status === 'active' ? 'Active' : 
                       subscription?.plan_status === 'expired' ? 'Expired' : 'Free'}
                    </span>
                  </div>
                  <div className="bg-slate-100/50 p-4 rounded-lg">
                    <p className="text-blue-700 text-xs uppercase tracking-wide mb-1">Start Date</p>
                    <p className="text-blue-900 font-medium">
                      {subscription?.plan_starts_at 
                        ? new Date(subscription.plan_starts_at).toLocaleDateString() 
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-slate-100/50 p-4 rounded-lg md:col-span-2">
                    <p className="text-blue-700 text-xs uppercase tracking-wide mb-1">
                      {subscription?.plan_status === 'active' ? 'Renews On' : 'Expiration Date'}
                    </p>
                    <p className="text-blue-900 font-medium">
                      {subscription?.plan_expires_at 
                        ? new Date(subscription.plan_expires_at).toLocaleDateString() 
                        : 'Never (Free Plan)'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-300 pt-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Your Features</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {getFeatures().map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle className="text-blue-600" size={16} />
                      <span className="text-blue-800 text-sm">{feature}</span>
                    </div>
                  ))}
                  {getFeatures().length === 0 && (
                    <p className="text-blue-700 text-sm">Basic features included</p>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-300 pt-6 mt-6">
                <Button onClick={handleOpenPlanModal} className="btn-primary">
                  {subscription?.plan_name?.toLowerCase().includes('free') 
                    ? 'Upgrade Your Plan' 
                    : 'Change Plan'
                  }
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Inbox Tab */}
          <TabsContent value="inbox" className="space-y-6">
            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-blue-900 mb-4">Recent Messages</h2>
              {messages.length > 0 ? (
                <div className="space-y-3">
                  {messages.map((msg, index) => (
                    <Link key={index} to={`/messages/${msg.matchId}`}>
                      <div className="flex items-center justify-between p-4 bg-slate-100/50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                            <span className="text-blue-600 font-semibold">
                              {msg.matchName?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div>
                            <h4 className="text-blue-900 font-medium">{msg.matchName}</h4>
                            <p className="text-blue-700 text-sm truncate max-w-xs">
                              {msg.lastMessage?.content || 'No messages yet'}
                            </p>
                          </div>
                        </div>
                        <ArrowRight className="text-blue-700" size={16} />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mail className="text-blue-700 mx-auto mb-4" size={48} />
                  <p className="text-blue-700">No messages yet</p>
                  <p className="text-blue-700 text-sm mt-1">Match with users to start conversations</p>
                  <Link to="/discovery">
                    <Button className="btn-primary mt-4">Discover Users</Button>
                  </Link>
                </div>
              )}
            </Card>

            {/* All Matches */}
            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-blue-900 mb-4">Your Matches</h2>
              {matches.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-3">
                  {matches.map((match) => (
                    <Link key={match.match_id} to={`/messages/${match.match_id}`}>
                      <div className="flex items-center gap-3 p-3 bg-slate-100/50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                          <span className="text-blue-600 font-semibold">
                            {match.matched_user?.name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-blue-900 font-medium">{match.matched_user?.name}</h4>
                          <p className="text-blue-700 text-xs capitalize">{match.matched_user?.role}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-blue-700 text-center py-4">No matches yet</p>
              )}
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-blue-900 mb-4">Account Information</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-blue-700 text-sm">Full Name</Label>
                  <Input 
                    value={user?.name || ''} 
                    disabled 
                    className="input-field mt-1 bg-slate-100/50" 
                  />
                </div>
                <div>
                  <Label className="text-blue-700 text-sm">Email</Label>
                  <Input 
                    value={user?.email || ''} 
                    disabled 
                    className="input-field mt-1 bg-slate-100/50" 
                  />
                </div>
                <div>
                  <Label className="text-blue-700 text-sm">Role</Label>
                  <Input 
                    value={user?.role || ''} 
                    disabled 
                    className="input-field mt-1 bg-slate-100/50 capitalize" 
                  />
                </div>
              </div>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-blue-900 mb-4">Privacy & Security</h2>
              <div className="space-y-4">
                <Link to="/privacy-policy" className="flex items-center justify-between p-3 bg-slate-100/50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Shield className="text-blue-700" size={20} />
                    <span className="text-blue-800">Privacy Policy</span>
                  </div>
                  <ArrowRight className="text-blue-700" size={16} />
                </Link>
                <Link to="/terms-of-use" className="flex items-center justify-between p-3 bg-slate-100/50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="text-blue-700" size={20} />
                    <span className="text-blue-800">Terms of Use</span>
                  </div>
                  <ArrowRight className="text-blue-700" size={16} />
                </Link>
              </div>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm p-6 border-red-500/30">
              <h2 className="text-xl font-bold text-blue-900 mb-4">Danger Zone</h2>
              <div className="space-y-4">
                <Button 
                  onClick={handleLogout} 
                  variant="outline" 
                  className="w-full border-slate-600 text-blue-800 hover:bg-slate-100"
                >
                  <LogOut className="mr-2" size={16} />
                  Log Out
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => toast.info('To delete your account, please contact hello@seedsynch.com')}
                >
                  <Trash2 className="mr-2" size={16} />
                  Request Account Deletion
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Plan Change Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-white border border-slate-200 shadow-sm p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-blue-900">Change Your Plan</h2>
              <Button onClick={() => setShowPlanModal(false)} variant="ghost" className="text-blue-700 p-2">
                <X size={20} />
              </Button>
            </div>

            {/* Current Plan */}
            <div className="bg-slate-100/50 p-4 rounded-lg mb-6">
              <p className="text-blue-700 text-sm">Current Plan</p>
              <p className="text-blue-900 font-semibold">{subscription?.plan_name || 'Free'}</p>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="mb-6">
              <Label className="text-blue-700 text-sm mb-2 block">Billing Cycle</Label>
              <div className="flex gap-2">
                <Button
                  onClick={() => setBillingCycle('monthly')}
                  variant={billingCycle === 'monthly' ? 'default' : 'outline'}
                  className={billingCycle === 'monthly' ? 'btn-primary' : 'text-blue-800'}
                >
                  Monthly
                </Button>
                <Button
                  onClick={() => setBillingCycle('yearly')}
                  variant={billingCycle === 'yearly' ? 'default' : 'outline'}
                  className={billingCycle === 'yearly' ? 'btn-primary' : 'text-blue-800'}
                >
                  Yearly (Save 20%)
                </Button>
              </div>
            </div>

            {/* Plan Options */}
            <div className="space-y-3 mb-6">
              {availablePlans.map((plan) => {
                const isCurrentPlan = plan.plan_name === subscription?.plan_name;
                const isSelected = selectedPlan?.plan_name === plan.plan_name;
                const price = getPrice(plan);
                
                return (
                  <div
                    key={plan.plan_name}
                    onClick={() => !isCurrentPlan && setSelectedPlan(plan)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isCurrentPlan 
                        ? 'border-slate-600 bg-slate-100/30 opacity-60 cursor-not-allowed' 
                        : isSelected 
                          ? 'border-blue-600 bg-blue-600/10 cursor-pointer' 
                          : 'border-slate-300 bg-slate-100/50 hover:border-slate-600 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-900 font-semibold">{plan.plan_name}</p>
                        {isCurrentPlan && (
                          <span className="text-xs text-blue-700">(Current Plan)</span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-blue-600 font-bold">
                          ${price}{billingCycle === 'yearly' ? '/yr' : '/mo'}
                        </p>
                        {price === 0 && <span className="text-xs text-blue-700">Free forever</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Plan Comparison - Features gained/lost */}
            {selectedPlan && (
              <PlanComparison 
                currentPlan={subscription?.plan_name || 'Free'}
                selectedPlan={selectedPlan}
                billingCycle={billingCycle}
              />
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handlePlanChange}
                disabled={!selectedPlan || upgrading}
                className="btn-primary flex-1"
              >
                {upgrading ? 'Updating...' : 'Confirm Change'}
              </Button>
              <Button
                onClick={() => setShowPlanModal(false)}
                variant="ghost"
                className="text-blue-800"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
